
import React, { useState, useMemo } from 'react'
import { TooltipProvider } from '@/components/ui/tooltip'
import type { CreatedItem, NodeType } from '../../types'
import { ImageLightbox } from '@/components/canvas/lightbox/ImageLightbox'
import { CreatedItemCard } from './CreatedItemCard'

interface CreatedContentProps {
  items: CreatedItem[]
  nodeType: NodeType
  selectedItemId?: string
  onItemSelect?: (item: CreatedItem) => void
  onItemDuplicate?: (item: CreatedItem) => void
  onItemDragOut?: (item: CreatedItem) => void
  onItemEdit?: (itemId: string, newContent: string) => void
  onItemDelete?: (itemId: string) => void
  onItemView?: (itemId: string) => void
  locked?: boolean
}

export function CreatedContent({
  items,
  nodeType,
  selectedItemId,
  onItemSelect,
  onItemDuplicate,
  onItemDragOut,
  onItemEdit,
  onItemDelete,
  onItemView,
  locked = false,
}: CreatedContentProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  // Prepare image items for lightbox - MUST be before early return
  // Use original version for lightbox (high quality), preview for thumbnails
  const imageItems = useMemo(() => {
    if (!items || items.length === 0) return []
    return items
      .filter((item) => item.type === 'image' && typeof item.content === 'string')
      .map((item) => ({
        id: item.id,
        url: item.metadata?.versions?.original?.url || item.content as string, // Use original for lightbox
        alt: item.metadata?.name || 'Generated image',
      }))
  }, [items])

  if (!items || items.length === 0) {
    return null
  }

  const handleImageView = (itemId: string) => {
    const index = imageItems.findIndex((img) => img.id === itemId)
    if (index !== -1) {
      setLightboxIndex(index)
      setLightboxOpen(true)
    }
    onItemView?.(itemId)
  }

  const isOddCount = items.length % 2 === 1
  const numRows = Math.ceil(items.length / 2)

  return (
    <div>
      {/* Created items grid with dividers */}
      <div className="relative">
        {/* Vertical divider - only show when NOT last odd item */}
        <div className="absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 bg-border" />

        {/* Horizontal dividers - create dividers between rows */}
        {numRows > 1 &&
          Array.from({ length: numRows - 1 }).map((_, rowIndex) => (
            <div
              key={`divider-${rowIndex}`}
              className="absolute right-0 left-0 h-px bg-border"
              style={{
                top: `${((rowIndex + 1) / numRows) * 100}%`,
                transform: 'translateY(-0.5px)',
              }}
            />
          ))}

        {/* Grid of items — single TooltipProvider for all cards (not one per card) */}
        <TooltipProvider delayDuration={150}>
        <div className="grid grid-cols-2" role="list" aria-label="Generated content items">
          {items.map((item, index) => {
            const isLastOddItem = isOddCount && index === items.length - 1
            const row = Math.floor(index / 2)
            const col = index % 2
            const isFirstRow = row === 0
            const isLastRow = row === numRows - 1

            return (
              <div
                key={item.id}
                role="listitem"
                aria-label={`Generated ${item.type} content`}
                className={`${
                  isLastOddItem ? 'col-span-2' : ''
                } ${
                  nodeType === 'image'
                    ? ''
                    : `${col === 0 ? 'pr-0.5' : 'pl-0.5'} ${!isFirstRow && !isLastRow ? 'py-0.5' : ''} ${isFirstRow && numRows > 1 ? 'pb-0.5' : ''} ${isLastRow && numRows > 1 ? 'pt-0.5' : ''}`
                }`}
              >
                <CreatedItemCard
                  item={item}
                  selected={selectedItemId === item.id}
                  onSelect={onItemSelect ? () => onItemSelect(item) : undefined}
                  onDuplicate={
                    onItemDuplicate ? () => onItemDuplicate(item) : undefined
                  }
                  onDragOut={
                    onItemDragOut ? () => onItemDragOut(item) : undefined
                  }
                  onEdit={
                    onItemEdit
                      ? (newContent?: string) => {
                          if (newContent) {
                            onItemEdit(item.id, newContent)
                          } else {
                            onItemEdit(
                              item.id,
                              typeof item.content === 'string'
                                ? item.content
                                : ''
                            )
                          }
                        }
                      : undefined
                  }
                  onDelete={
                    onItemDelete ? () => onItemDelete(item.id) : undefined
                  }
                  onView={
                    item.type === 'image' ? () => handleImageView(item.id) : undefined
                  }
                  locked={locked}
                />
              </div>
            )
          })}
        </div>
        </TooltipProvider>
      </div>

      {/* Image Lightbox */}
      <ImageLightbox
        images={imageItems}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  )
}
